import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, CalendarRange, CalendarDays, Plus, Video } from "lucide-react";
import { listarTarefas } from "../services/tarefas";
import { listarUsuariosAtivos } from "../services/usuarios";
import { NovaTarefaModal } from "./tarefas/NovaTarefaModal";
import { useAuth } from "../hooks/useAuth";

// MELHORIA 20 — Agenda da equipe: as TAREFAS num calendário, filtráveis por
// pessoa (o gestor controla a agenda de cada um), coloridas por PRIORIDADE, e
// com "agendar" num dia (cria tarefa, inclusive Captação, atribuída a alguém).

const PRIO: Record<string, { cor: string; label: string; ordem: number }> = {
  urgente: { cor: "#dc2626", label: "Urgente", ordem: 0 },
  alta: { cor: "#d97706", label: "Alta", ordem: 1 },
  media: { cor: "#2563eb", label: "Média", ordem: 2 },
  baixa: { cor: "#64748b", label: "Baixa", ordem: 3 },
};
const prioDe = (p?: string) => PRIO[p || "media"] || PRIO.media;

function parseDataLocal(s: string): Date {
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}
function ymd(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function horaDe(iso: string, comHora?: boolean): string {
  if (!comHora) return "";
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

type Modo = "semana" | "mes";
const HEADER_DIAS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

export function AgendaEquipe() {
  const { can } = useAuth();
  const podeVer = can(["gestor", "admin"]);
  const hoje = new Date();

  const [modo, setModo] = useState<Modo>("semana");
  const [referencia, setReferencia] = useState(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()));
  const [pessoaId, setPessoaId] = useState(""); // "" = todas as pessoas
  const [novo, setNovo] = useState<{ prazo?: string; atribuidoA?: string; tipoSlug?: string } | null>(null);

  // Key começa com "tarefas" de propósito: o NovaTarefaModal invalida ["tarefas"]
  // (match por prefixo), então agendar uma tarefa recarrega a agenda na hora.
  const { data: tarefas = [], isLoading } = useQuery({ queryKey: ["tarefas", "agenda"], queryFn: () => listarTarefas(), enabled: podeVer });
  const { data: usuarios = [] } = useQuery({ queryKey: ["usuarios-ativos"], queryFn: listarUsuariosAtivos, enabled: podeVer });

  const hojeIso = ymd(hoje);

  // Tarefas com prazo, não arquivadas, da pessoa selecionada (principal OU co-responsável).
  const itens = useMemo(() => {
    const daPessoa = (t: any) => !pessoaId || t.atribuidoA === pessoaId || (t.responsaveis || []).some((r: any) => r.usuarioId === pessoaId);
    return tarefas
      .filter((t) => !t.arquivada && t.prazo && daPessoa(t))
      .map((t) => ({ t, data: parseDataLocal(t.prazo) }));
  }, [tarefas, pessoaId]);

  // Range visível (semana = 7 dias; mês = semanas completas).
  const { dias, label } = useMemo(() => {
    if (modo === "semana") {
      const dow = referencia.getDay();
      const ini = new Date(referencia.getFullYear(), referencia.getMonth(), referencia.getDate() - dow);
      const fim = new Date(ini.getFullYear(), ini.getMonth(), ini.getDate() + 6);
      const arr = Array.from({ length: 7 }, (_, i) => new Date(ini.getFullYear(), ini.getMonth(), ini.getDate() + i));
      return { dias: arr, label: `${ini.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })} – ${fim.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })}` };
    }
    const primeiro = new Date(referencia.getFullYear(), referencia.getMonth(), 1);
    const ultimo = new Date(referencia.getFullYear(), referencia.getMonth() + 1, 0);
    const ini = new Date(primeiro.getFullYear(), primeiro.getMonth(), primeiro.getDate() - primeiro.getDay());
    const fim = new Date(ultimo.getFullYear(), ultimo.getMonth(), ultimo.getDate() + (6 - ultimo.getDay()));
    const total = Math.round((fim.getTime() - ini.getTime()) / 86400000) + 1;
    const arr = Array.from({ length: total }, (_, i) => new Date(ini.getFullYear(), ini.getMonth(), ini.getDate() + i));
    return { dias: arr, label: primeiro.toLocaleDateString("pt-BR", { month: "long", year: "numeric" }) };
  }, [modo, referencia]);

  const porDia = useMemo(() => {
    const mapa = new Map<string, typeof itens>();
    for (const it of itens) {
      const k = ymd(it.data);
      const arr = mapa.get(k) || [];
      arr.push(it);
      mapa.set(k, arr as any);
    }
    for (const arr of mapa.values()) {
      arr.sort((a, b) => {
        const pa = prioDe(a.t.prioridade).ordem, pb = prioDe(b.t.prioridade).ordem;
        if (pa !== pb) return pa - pb; // urgente primeiro
        return new Date(a.t.prazo).getTime() - new Date(b.t.prazo).getTime();
      });
    }
    return mapa;
  }, [itens]);

  function navegar(delta: number) {
    const d = new Date(referencia);
    if (modo === "semana") d.setDate(d.getDate() + delta * 7);
    else d.setMonth(d.getMonth() + delta);
    setReferencia(d);
  }

  if (!podeVer) return <div className="text-slate-500 text-sm">Acesso restrito à gestão.</div>;

  const nomePessoa = pessoaId ? usuarios.find((u) => u.id === pessoaId)?.nome : "toda a equipe";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5">
          <button onClick={() => setModo("semana")} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md ${modo === "semana" ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm" : "text-slate-500 dark:text-slate-400"}`}>
            <CalendarRange size={12} /> Semana
          </button>
          <button onClick={() => setModo("mes")} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md ${modo === "mes" ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm" : "text-slate-500 dark:text-slate-400"}`}>
            <CalendarDays size={12} /> Mês
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Ver agenda de: uma pessoa por vez (ou todas). */}
          <select
            value={pessoaId}
            onChange={(e) => setPessoaId(e.target.value)}
            className="text-xs border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-lg px-2.5 py-2"
            title="Ver a agenda de uma pessoa (ou de todos)"
          >
            <option value="">👥 Toda a equipe</option>
            {usuarios.map((u) => (
              <option key={u.id} value={u.id}>{u.nome}</option>
            ))}
          </select>
          <button
            onClick={() => setNovo({ prazo: hojeIso, atribuidoA: pessoaId || undefined, tipoSlug: "captacao" })}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-purple-600 hover:bg-purple-700 text-white"
            title="Agendar uma captação (vira tarefa) — escolha o dia e a pessoa"
          >
            <Video size={14} /> Agendar captação
          </button>
          <div className="flex items-center gap-1.5">
            <button onClick={() => navegar(-1)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"><ChevronLeft size={16} /></button>
            <span className="text-sm font-medium text-slate-800 dark:text-slate-100 capitalize px-2 min-w-[10rem] text-center">{label}</span>
            <button onClick={() => navegar(1)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"><ChevronRight size={16} /></button>
            <button onClick={() => setReferencia(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()))} className="ml-1 px-3 py-2 rounded-lg text-xs font-medium bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200">Hoje</button>
          </div>
        </div>
      </div>

      {/* Legenda de prioridade + quem está sendo visto */}
      <div className="flex flex-wrap items-center gap-2.5 text-xs text-slate-600 dark:text-slate-400">
        <span className="text-slate-500 dark:text-slate-400">Agenda de <strong className="text-slate-700 dark:text-slate-200">{nomePessoa}</strong> · prioridade:</span>
        {Object.values(PRIO).map((p) => (
          <span key={p.label} className="inline-flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: p.cor }} />
            {p.label}
          </span>
        ))}
      </div>

      {isLoading && <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-8 text-center text-slate-400 text-sm">Carregando agenda…</div>}

      {!isLoading && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="grid grid-cols-7 border-b border-slate-200 dark:border-slate-700">
            {HEADER_DIAS.map((d, i) => {
              const ehFds = i === 0 || i === 6;
              return <div key={d} className={`px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide ${ehFds ? "bg-slate-200 dark:bg-slate-900/70 text-slate-500" : "bg-slate-50 dark:bg-slate-900/50 text-slate-600 dark:text-slate-300"}`}>{d}</div>;
            })}
          </div>
          <div className="grid grid-cols-7">
            {dias.map((dia, idx) => {
              const ehHoje = ymd(dia) === hojeIso;
              const ehMesAtual = modo === "mes" ? dia.getMonth() === referencia.getMonth() : true;
              const dow = dia.getDay();
              const ehFds = dow === 0 || dow === 6;
              const doDia = porDia.get(ymd(dia)) || [];
              const maxVisiveis = modo === "semana" ? 10 : 4;
              const visiveis = doDia.slice(0, maxVisiveis);
              const restantes = doDia.length - visiveis.length;
              const bg = ehFds ? "bg-slate-100 dark:bg-slate-900/60" : ehMesAtual ? "bg-white dark:bg-slate-800" : "bg-slate-50/70 dark:bg-slate-900/40";
              return (
                <div key={idx} className={`group border-b border-r border-slate-100 dark:border-slate-700 ${idx % 7 === 0 ? "border-l" : ""} ${modo === "semana" ? "min-h-[240px]" : "min-h-[120px]"} p-1.5 flex flex-col gap-0.5 ${bg}`}>
                  <div className="flex items-center justify-between px-1">
                    <span className={`text-[11px] font-medium inline-flex items-center justify-center ${ehHoje ? "bg-blue-600 text-white rounded-full w-6 h-6" : ehFds ? "text-slate-400" : ehMesAtual ? "text-slate-700 dark:text-slate-200" : "text-slate-400"}`}>{dia.getDate()}</span>
                    <button
                      onClick={() => setNovo({ prazo: ymd(dia), atribuidoA: pessoaId || undefined })}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                      title={`Agendar tarefa em ${dia.toLocaleDateString("pt-BR")}${pessoaId ? ` para ${nomePessoa}` : ""}`}
                    >
                      <Plus size={13} />
                    </button>
                  </div>
                  <div className="flex flex-col gap-0.5 mt-0.5">
                    {visiveis.map(({ t }) => {
                      const p = prioDe(t.prioridade);
                      const concluida = t.status === "concluido";
                      const hora = horaDe(t.prazo, (t as any).prazoComHora);
                      return (
                        <Link
                          key={t.id}
                          to={`/tarefas?tarefa=${t.id}`}
                          className={`text-[10px] px-1.5 py-0.5 rounded truncate font-medium text-white hover:opacity-90 flex items-center gap-1 ${concluida ? "opacity-50 line-through" : ""}`}
                          style={{ background: p.cor }}
                          title={`${p.label} · ${t.titulo}${t.atribuido?.nome ? ` · ${t.atribuido.nome}` : ""}${hora ? ` · ${hora}` : ""}`}
                        >
                          {t.tipoTarefa?.icone && <span className="shrink-0">{t.tipoTarefa.icone}</span>}
                          {hora && <span className="shrink-0 tabular-nums">{hora}</span>}
                          <span className="truncate">{t.titulo}</span>
                          {!pessoaId && t.atribuido?.nome && <span className="truncate opacity-80">· {t.atribuido.nome.split(" ")[0]}</span>}
                        </Link>
                      );
                    })}
                    {restantes > 0 && <span className="text-[10px] px-1.5 py-0.5 text-slate-500 font-medium">+{restantes} mais</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {novo && (
        <NovaTarefaModal
          prefill={novo}
          onClose={() => setNovo(null)}
        />
      )}
    </div>
  );
}
